// This hook exposes auth status and a lean user data from users collection
// provides firebaseLogin, cleverLogin, logout actions
// Warm the bootstrap cache immdiately after login so useBootstrap()
// and useUser() find data on first render with no loading state

import { useContext } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { AuthContext } from "./AuthContext";
import api, { setAccessToken } from "@/api/client";
import { bootstrapQueryKey } from "../bootstrap/bootstrapQuery";
import { clearAccessToken } from "@/api/client";
import { useNavigate } from "react-router-dom";
import { getAuth, signOut } from "firebase/auth";
import { AUTH_EVENTS } from "./authReducer";
import { dedupedLogin } from "./authController";

export function useAuth() {
  const ctx = useContext(AuthContext);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  if (!ctx) {
    throw new Error("useAuth must be used inside AuthProvider");
  }

  const { state, dispatch } = ctx;

  async function prefetchBootstrap(userId) {
    // Warms the bootstrap cache before navigation so useBootstrap
    // finds data immediately on first render - no loading state.
    await queryClient.prefetchQuery({
      queryKey: bootstrapQueryKey(userId),
      queryFn: async () => {
        const res = await api.get("/bootstrap");
        return res.data;
      },
      staleTime: 1000 * 60 * 60,
    });
  }

  async function firebaseLogin(idToken) {
    dispatch({ type: AUTH_EVENTS.LOGIN_START });

    try {
      return await dedupedLogin(async () => {
        const res = await api.post("/auth/firebase", { idToken });
        setAccessToken(res.data.accessToken);

        const firebaseUser = getAuth().currentUser;
        const photoURL = firebaseUser?.photoURL ?? null;
        dispatch({
          type: AUTH_EVENTS.LOGIN_SUCCESS,
          user: res.data.user,
          photoURL,
        });

        try {
          await prefetchBootstrap(res.data.user._id.toString());
        } catch (err) {
          console.warn(
            "[firebase] Bootstrap prefetch failed silently - will retry on mount:",
            err,
          );
        }
      });
    } catch (err) {
      // Without this the status stays on LOGGING_IN and AuthGate renders the
      // session-restoring spinner forever - the caller's own catch can't help,
      // because by then it has been unmounted. Moving to a terminal state lets
      // the screen come back and offer a retry. Rethrown so callers still see
      // the failure.
      dispatch({ type: AUTH_EVENTS.LOGIN_FAIL, error: err });
      throw err;
    }
  }

  async function cleverLogin(code) {
    dispatch({ type: AUTH_EVENTS.LOGIN_START });

    return dedupedLogin(async () => {
      const res = await api.post("/auth/clever", { code });
      setAccessToken(res.data.accessToken);

      dispatch({
        type: AUTH_EVENTS.LOGIN_SUCCESS,
        user: res.data.user,
        photoURL: null,
      });

      try {
        await prefetchBootstrap(res.data.user._id.toString());
      } catch (err) {
        console.warn(
          "[Clever] Bootstrap prefetch failed silently - will retry on mount:",
          err,
        );
      }
    });
  }

  async function logout() {
    try {
      await api.post("/auth/logout");
    } finally {
      await signOut(getAuth());
      clearAccessToken();
      queryClient.clear();
      dispatch({ type: AUTH_EVENTS.LOGOUT });
      navigate("/", { replace: true });
    }
  }

  return {
    status: state.status,
    user: state.user,
    photoURL: state.photoURL,
    firebaseLogin,
    cleverLogin,
    logout,
  };
}
